/**
 * Fetch wrapper for `/v1`. Every write carries `X-Requested-With: deepthird`
 * (the API's CSRF rule); the session is the HttpOnly cookie.
 */
import { noteError } from "../lib/errlog";
import type { ApiErrorBody } from "./types";

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, message: string, readonly details?: Record<string, unknown>) {
    super(message);
  }
}

export const CSRF = { "X-Requested-With": "deepthird" };

export async function api<T>(method: string, path: string, body?: unknown, headers: Record<string, string> = {}): Promise<T> {
  const init: RequestInit = { method, headers: { ...CSRF, ...headers }, credentials: "same-origin" };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
    (init.headers as Record<string, string>)["Content-Type"] = "application/json";
  }
  let res: Response;
  try {
    res = await fetch(path, init);
  } catch (e) {
    noteError(`${method} ${path}`, (e as Error).message);
    throw e;
  }
  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }
  if (!res.ok) {
    const e = (parsed as ApiErrorBody | null)?.error;
    const err = new ApiError(res.status, e?.code ?? "HTTP_" + res.status, e?.message ?? res.statusText, e?.details);
    // 401 is how a signed-out page learns it is signed out, not an error
    if (res.status !== 401) noteError(`${method} ${path}`, `${res.status} ${err.code}: ${err.message}`);
    throw err;
  }
  return parsed as T;
}

export const get = <T>(path: string) => api<T>("GET", path);
export const post = <T>(path: string, body?: unknown, headers?: Record<string, string>) => api<T>("POST", path, body, headers);
export const patch = <T>(path: string, body?: unknown, headers?: Record<string, string>) => api<T>("PATCH", path, body, headers);
export const put = <T>(path: string, body?: unknown, headers?: Record<string, string>) => api<T>("PUT", path, body, headers);
export const del = <T>(path: string) => api<T>("DELETE", path);

/** Dev-only backend routes (apps/api/dev/server.ts). Present only when the app runs against it. */
export const dev = {
  enabled: import.meta.env.DEV || import.meta.env.VITE_DEV_BACKEND === "1",
  login: (email: string, plan: string, name?: string, handle?: string) => post<{ user: { id: string } }>("/dev/login", { email, plan, name, ...(handle ? { handle } : {}) }),
  seed: () => post<{ seeded: Record<string, string> }>("/dev/seed"),
  advance: (videoId: string) => post<{ from: string; to: string; advanced: boolean }>(`/dev/videos/${videoId}/advance`),
  finishExport: (id: string) => post<{ ok: boolean }>(`/dev/exports/${id}/finish`),
  completeCheckout: (subscriptionId: string) => post<{ webhook_status: number; plan_code: string }>("/dev/checkout/complete", { subscription_id: subscriptionId }),
};
